import { Dialog, DialogActions, DialogContent, Drawer } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import MDButton from 'components/MDButton';
import KnowledgeBaseDetails from 'pages/knowledgeBase/components/knowledgeBaseDetails';
import { useNotification } from 'hook/useNotification';
import {
  useCreateKnowledgeBaseMutation,
  useUpdateKnowledgeBaseMutation
} from 'services/knowledgeBaseApi';

function KnowledgeBaseDetailsDrawer({ knowledgeBase, drawerOpen, setDrawerOpen }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [createKnowledgeBase] = useCreateKnowledgeBaseMutation();
  const [updateKnowledgeBase] = useUpdateKnowledgeBaseMutation();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDrawerOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  return (
    <>
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: { width: 700, p: 2 }
        }}
      >
        <KnowledgeBaseDetails
          knowledgeBase={knowledgeBase}
          onSubmit={handleFormSubmit}
          onFieldChange={handleFieldChange}
        />
      </Drawer>
      <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelClose} color="secondary">
            {msg({ id: 'button.cancel' })}
          </MDButton>
          <MDButton onClick={handleCloseDrawer} color="primary">
            {msg({ id: 'button.discard' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );

  function handleCreate(data) {
    createKnowledgeBase({ ...data })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.knowledgeBase.update.success' })
        });
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.knowledgeBase.update.error' })
        });
      });
  }

  function handleUpdate(data) {
    updateKnowledgeBase({
      id: data.id,
      knowledgeBaseRequest: { ...data }
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.knowledgeBase.update.success' })
        });
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.knowledgeBase.update.error' })
        });
      });
  }
}

KnowledgeBaseDetailsDrawer.propTypes = {
  knowledgeBase: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string
  }),
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired
};

export default KnowledgeBaseDetailsDrawer;
